import { Composer,   Popover,
  PopoverContent,
  PopoverTrigger, EmojiPicker, IconButton } from "@somethings-ui/web";
import { Camera, Loader2, Smile, X } from "lucide-react";
import { GifIcon, LightBulbIcon } from "@heroicons/react/24/outline";
import { useCallback, useRef, useState } from "react";
import type { ChangeEvent, DragEvent, KeyboardEvent } from "react";
import { GifPicker } from "@/components/conversations/gif-picker";
import { insertAtCursor } from "./attention-composer-insert";
import { AttentionBannerView } from "./attention-banner-view";
import type {
  AttentionComposerBanner,
  AttentionComposerEditor,
  AttentionComposerGif,
  AttentionComposerImage,
  AttentionComposerModeControls,
  AttentionComposerQuote,
  AttentionComposerSuggestions,
  AttentionComposerVoice,
} from "./attention-composer-model";

export interface AttentionComposerProps {
  editor: AttentionComposerEditor;
  quote: AttentionComposerQuote;
  mode: AttentionComposerModeControls;
  image: AttentionComposerImage;
  gif: AttentionComposerGif;
  voice: AttentionComposerVoice;
  suggestions?: AttentionComposerSuggestions;
  banner?: AttentionComposerBanner;
}

export function AttentionComposer({
  editor,
  quote,
  mode,
  image,
  gif,
  voice,
  suggestions,
  banner,
}: AttentionComposerProps) {
  const {
    draft,
    onDraftChange,
    onSend,
    canSend,
    errorMessage = null,
    placeholder = "Your message...",
    disabled = false,
  } = editor;
  const { value: quoteValue, onClear: onClearQuote } = quote;
  const {
    current: activeMode,
    variant,
    onToggle: onToggleMode,
    onClose: onCloseMode,
  } = mode;
  const {
    preview: imagePreview,
    kind: imageKind,
    converting: imageConverting,
    onSelectFile: onSelectImageFile,
    onClear: onClearImage,
  } = image;
  const {
    selected: selectedGif,
    onSelect: onSelectGif,
    onClear: onClearGif,
  } = gif;
  const {
    view: voiceView,
    onStart: onStartVoice,
    onStop: onStopVoice,
    onPause: onPauseVoice,
    onResume: onResumeVoice,
    onDiscard: onDiscardVoice,
  } = voice;
  const canSuggest = suggestions?.enabled ?? false;
  const suggestionsSlot = suggestions?.slot ?? null;
  const bannerDescriptor = banner?.descriptor ?? null;
  const bannerOpen = banner?.open ?? true;
  const onBannerToggle = banner?.onToggle;
  const onBannerAction = banner?.onAction;
  const onInsertMagicMoment = banner?.onInsertMagicMoment;

  const inputRef = useRef<HTMLTextAreaElement>(null);

  // GifPicker fires onClose after every selection; this flag lets a real
  // dismissal through without clearing the just-staged preview.
  const gifJustSelectedRef = useRef(false);

  const handleGifSelect = useCallback(
    (url: string) => {
      gifJustSelectedRef.current = true;
      onSelectGif(url);
    },
    [onSelectGif],
  );

  const handleGifPickerClose = useCallback(() => {
    if (gifJustSelectedRef.current) {
      gifJustSelectedRef.current = false;
      return;
    }
    onCloseMode();
  }, [onCloseMode]);

  const resize = useCallback((element: HTMLTextAreaElement) => {
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      onDraftChange(event.target.value);
      resize(event.currentTarget);
    },
    [onDraftChange, resize],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        event.preventDefault();
        if (canSend) onSend();
      }
    },
    [canSend, onSend],
  );

  const capturingVoice =
    voiceView.status === "recording" || voiceView.status === "paused";
  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      const el = inputRef.current;
      const start = el?.selectionStart ?? draft.length;
      const end = el?.selectionEnd ?? draft.length;
      const { next, caret } = insertAtCursor(draft, emoji, start, end);
      onDraftChange(next);
      onCloseMode();
      requestAnimationFrame(() => {
        if (!el) return;
        el.focus();
        el.setSelectionRange(caret, caret);
        resize(el);
      });
    },
    [draft, onCloseMode, onDraftChange, resize],
  );

  const handleEmojiOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        if (activeMode !== "emoji") onToggleMode("emoji");
        return;
      }
      if (activeMode === "emoji") onCloseMode();
    },
    [activeMode, onToggleMode, onCloseMode],
  );

  const panelOpen =
    activeMode === "image" ||
    (activeMode === "gif" && selectedGif !== null) ||
    activeMode === "suggestions" ||
    quoteValue !== null;
  const showBanner = Boolean(bannerDescriptor) && !panelOpen;

  return (
    <div className="shrink-0 p-4">
      <Composer variant={variant}>
        {showBanner && bannerDescriptor ? (
          <AttentionBannerView
            descriptor={bannerDescriptor}
            open={bannerOpen}
            onToggle={onBannerToggle}
            onAction={onBannerAction}
            onInsertMagicMoment={onInsertMagicMoment}
          />
        ) : null}

        {activeMode === "suggestions" && canSuggest ? (
          <Composer.Panel
            onDismiss={onCloseMode}
            dismissLabel="Close suggestions"
          >
            {suggestionsSlot}
          </Composer.Panel>
        ) : null}

        {quoteValue ? (
          <Composer.Panel onDismiss={onClearQuote} dismissLabel="Remove reply">
            <Composer.Reply
              label={`Replying to ${quoteValue.senderLabel}`}
              thumbnail={
                quoteValue.thumbnailUrl ? (
                  <img
                    src={quoteValue.thumbnailUrl}
                    alt=""
                    className="size-full object-cover"
                  />
                ) : undefined
              }
            >
              {quoteValue.text}
            </Composer.Reply>
          </Composer.Panel>
        ) : null}

        {activeMode === "image" ? (
          <Composer.Panel onDismiss={onCloseMode} dismissLabel="Cancel photo">
            {imageConverting ? (
              <div className="flex flex-col items-center gap-2 px-4 pb-6 pt-4">
                <Loader2
                  className="size-6 animate-spin text-icon-secondary"
                  aria-hidden
                />
                <p className="font-body text-body-2 text-text-secondary">
                  Converting…
                </p>
              </div>
            ) : imagePreview ? (
              <div className="flex flex-col items-center gap-4 px-4 pb-6 pt-4">
                <div className="relative">
                  {imageKind === "video" ? (
                    <video
                      src={imagePreview}
                      controls
                      muted
                      playsInline
                      className="max-h-40 rounded-xl object-cover"
                    />
                  ) : (
                    <img
                      src={imagePreview}
                      alt="Selected preview"
                      className="max-h-40 rounded-xl object-cover"
                    />
                  )}
                  <IconButton
                    variant="secondary"
                    shape="circle"
                    size="sm"
                    aria-label={
                      imageKind === "video" ? "Remove video" : "Remove image"
                    }
                    onClick={onClearImage}
                    className="absolute -right-2 -top-2"
                    icon={<X className="size-4" aria-hidden />}
                  />
                </div>
              </div>
            ) : (
              <ImageDropZone onSelectFile={onSelectImageFile} />
            )}
          </Composer.Panel>
        ) : null}

        {activeMode === "gif" && selectedGif ? (
          <Composer.Panel onDismiss={onCloseMode} dismissLabel="Cancel GIF">
            <div className="flex flex-col items-center gap-4 px-4 pb-6 pt-4">
              <div className="relative">
                <img
                  src={selectedGif}
                  alt="Selected GIF preview"
                  className="max-h-40 rounded-xl object-cover"
                />
                <IconButton
                  variant="secondary"
                  shape="circle"
                  size="sm"
                  aria-label="Remove GIF"
                  onClick={onClearGif}
                  className="absolute -right-2 -top-2"
                  icon={<X className="size-4" aria-hidden />}
                />
              </div>
            </div>
          </Composer.Panel>
        ) : null}

        <Composer.Card>
          {capturingVoice ? (
            <Composer.VoiceRecorder
              amplitudes={voiceView.amplitudes}
              time={voiceView.time}
              paused={voiceView.status === "paused"}
              onDone={onStopVoice}
            />
          ) : voiceView.status === "recorded" && voiceView.recording ? (
            <Composer.VoicePreview
              amplitudes={voiceView.recording.amplitudes}
              time={voiceView.time}
              src={voiceView.recording.url}
              onReRecord={onStartVoice}
              onDiscard={onDiscardVoice}
            />
          ) : (
            <Composer.Input
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              aria-label="Message"
              className="subtle-scrollbar max-h-40 overflow-y-auto"
            />
          )}
          <Composer.Toolbar>
            <Composer.ToolbarGroup>
              <Composer.VoiceToolbarButton
                status={voiceView.status}
                disabled={disabled || !voiceView.isSupported}
                onStart={onStartVoice}
                onPause={onPauseVoice}
                onResume={onResumeVoice}
              />
              <Composer.ToolbarButton
                aria-label="Add photo or video"
                active={activeMode === "image"}
                disabled={disabled}
                onClick={() => onToggleMode("image")}
                icon={<Camera className="size-6" />}
              />
              <Popover
                open={activeMode === "emoji"}
                onOpenChange={handleEmojiOpenChange}
              >
                <PopoverTrigger asChild>
                  <Composer.ToolbarButton
                    aria-label="Add emoji"
                    active={activeMode === "emoji"}
                    disabled={disabled}
                    icon={<Smile className="size-6" />}
                  />
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="start"
                  className="w-fit border-0 bg-transparent p-0 shadow-none"
                >
                  <EmojiPicker onEmojiSelect={handleEmojiSelect} />
                </PopoverContent>
              </Popover>
              <Composer.ToolbarButton
                aria-label="Add GIF"
                active={activeMode === "gif"}
                disabled={disabled}
                onClick={() => onToggleMode("gif")}
                icon={<GifIcon className="size-6" />}
              />
              {canSuggest ? (
                <Composer.ToolbarButton
                  aria-label="Suggested messages"
                  active={activeMode === "suggestions"}
                  disabled={disabled}
                  onClick={() => onToggleMode("suggestions")}
                  icon={<LightBulbIcon className="size-6" />}
                />
              ) : null}
            </Composer.ToolbarGroup>
            <Composer.SendButton disabled={!canSend} onClick={onSend} />
          </Composer.Toolbar>
        </Composer.Card>

        {errorMessage ? (
          <p className="px-4 pt-1 font-body text-body-3 text-states-error">
            {errorMessage}
          </p>
        ) : null}
      </Composer>

      <GifPicker
        isOpen={activeMode === "gif" && selectedGif === null}
        onClose={handleGifPickerClose}
        onGifSelect={handleGifSelect}
      />
    </div>
  );
}

function ImageDropZone({
  onSelectFile,
}: {
  onSelectFile: (file: File | null | undefined) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const openPicker = useCallback(() => fileInputRef.current?.click(), []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openPicker();
      }
    },
    [openPicker],
  );

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onSelectFile(event.target.files?.[0]);
      event.target.value = "";
    },
    [onSelectFile],
  );

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragActive(false);
      onSelectFile(event.dataTransfer.files?.[0]);
    },
    [onSelectFile],
  );

  return (
    <>
      <Composer.ImageDrop
        role="button"
        tabIndex={0}
        dragActive={dragActive}
        onClick={openPicker}
        onKeyDown={handleKeyDown}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className="cursor-pointer"
        label="Choose an image or video or drag and drop it here"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,.heic,.heif"
        className="hidden"
        onChange={handleFileChange}
      />
    </>
  );
}
